import type { RouteDraft, RouteDraftField } from './types';

export type SetScopeAction = { type: 'SET_SCOPE'; firmId: string | undefined; routeId: string };
export const setScope = (firmId: string | undefined, routeId: string): SetScopeAction => ({
    type: 'SET_SCOPE',
    firmId,
    routeId,
});

export type SetCanManageAction = { type: 'SET_CAN_MANAGE'; canManage: boolean };
export const setCanManage = (canManage: boolean): SetCanManageAction => ({ type: 'SET_CAN_MANAGE', canManage });

/** The server copy of the route changed (initial load, refetch, or our own save landing). */
export type SyncRouteAction = { type: 'SYNC_ROUTE'; baseline: RouteDraft };
export const syncRoute = (baseline: RouteDraft): SyncRouteAction => ({ type: 'SYNC_ROUTE', baseline });

export type SetFieldAction = {
    [K in RouteDraftField]: { type: 'SET_FIELD'; field: K; value: RouteDraft[K] };
}[RouteDraftField];
export const setField = <K extends RouteDraftField>(field: K, value: RouteDraft[K]): SetFieldAction =>
    ({ type: 'SET_FIELD', field, value }) as SetFieldAction;

export type ToggleProviderAction = { type: 'TOGGLE_PROVIDER'; providerId: string };
export const toggleProvider = (providerId: string): ToggleProviderAction => ({ type: 'TOGGLE_PROVIDER', providerId });

export type SetBidPriorityAction = { type: 'SET_BID_PRIORITY'; providerId: string; bidPriority: number | null };
export const setBidPriority = (providerId: string, bidPriority: number | null): SetBidPriorityAction => ({
    type: 'SET_BID_PRIORITY',
    providerId,
    bidPriority,
});

/** Target every listed provider, keeping priorities of those already targeted. */
export type TargetProvidersAction = { type: 'TARGET_PROVIDERS'; providerIds: string[] };
export const targetProviders = (providerIds: string[]): TargetProvidersAction => ({
    type: 'TARGET_PROVIDERS',
    providerIds,
});

/** Untarget every listed provider; targets outside the list (foreign/invalid) are kept. */
export type UntargetProvidersAction = { type: 'UNTARGET_PROVIDERS'; providerIds: string[] };
export const untargetProviders = (providerIds: string[]): UntargetProvidersAction => ({
    type: 'UNTARGET_PROVIDERS',
    providerIds,
});

export type SetProviderSearchAction = { type: 'SET_PROVIDER_SEARCH'; value: string };
export const setProviderSearch = (value: string): SetProviderSearchAction => ({ type: 'SET_PROVIDER_SEARCH', value });

export type DiscardAction = { type: 'DISCARD' };
export const discard = (): DiscardAction => ({ type: 'DISCARD' });

export type StartSaveAction = { type: 'START_SAVE' };
export const startSave = (): StartSaveAction => ({ type: 'START_SAVE' });

/** The PATCH succeeded; the saved draft becomes the baseline until the refetch confirms it. */
export type FinishSaveAction = { type: 'FINISH_SAVE'; saved: RouteDraft };
export const finishSave = (saved: RouteDraft): FinishSaveAction => ({ type: 'FINISH_SAVE', saved });

export type FailSaveAction = { type: 'FAIL_SAVE' };
export const failSave = (): FailSaveAction => ({ type: 'FAIL_SAVE' });

export type LabRouteEditorAction =
    | SetScopeAction
    | SetCanManageAction
    | SyncRouteAction
    | SetFieldAction
    | ToggleProviderAction
    | SetBidPriorityAction
    | TargetProvidersAction
    | UntargetProvidersAction
    | SetProviderSearchAction
    | DiscardAction
    | StartSaveAction
    | FinishSaveAction
    | FailSaveAction;
